import { useMemo } from "react";
import { Link } from "react-router-dom";

import Header from "../../components/layout/Header/Header";
import Footer from "../../components/layout/Footer/Footer";
import Container from "../../components/layout/Container/Container";

import s from "./TeamPage.module.scss";

import { useEmployees } from "../../api/queries/useEmployees";
import { useGallery } from "../../api/queries/useGallery";
import { useEmployeeWorks } from "../../api/queries/useEmployeeWorks";

import type { components } from "../../api/schema";
import type { EmployeeWork } from "../../api/employeeWorks";

type Employee = components["schemas"]["Employee"];
type Gallery = components["schemas"]["Gallery"];

function safeImgSrc(path?: string) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return path;
  return `/${path}`;
}

function fullName(e: Employee) {
  const parts = [e.surname, e.name, (e as any).patronymic].filter(Boolean);
  return parts.join(" ").trim() || "Сотрудник";
}

function getAvatarId(e: Employee): number | undefined {
  const anyE = e as any;
  const v = anyE.avatar_path_id ?? anyE.image_path_id;
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

function splitMetaLines(...parts: Array<string | undefined>) {
  const raw = parts.filter(Boolean).join("\n");
  return raw
    .split(/\n|;|,/g)
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 3);
}

function getIdArray(e: Employee, key: "work_path_id") {
  const v = (e as any)[key];
  if (!Array.isArray(v)) return [];
  return v.filter((x) => typeof x === "number" && Number.isFinite(x) && x > 0);
}

function EmployeePhoto({
  photoId,
  name,
  galleryMap,
}: {
  photoId?: number;
  name: string;
  galleryMap: Map<number, Gallery>;
}) {
  const g = photoId ? galleryMap.get(photoId) : undefined;
  const src = safeImgSrc(g?.image_path);

  if (!src) {
    return (
      <div className={s.photoFallback} aria-hidden="true">
        {name.slice(0, 1).toUpperCase()}
      </div>
    );
  }

  return <img className={s.photoImg} src={src} alt={name} loading="lazy" />;
}

function EmployeeCard({
  e,
  galleryMap,
  worksEmployeeIds,
}: {
  e: Employee;
  galleryMap: Map<number, Gallery>;
  worksEmployeeIds: Set<number>;
}) {
  const name = fullName(e);
  const photoId = getAvatarId(e);

  const position = (e as any).position_at_work as string | undefined;
  const lines = splitMetaLines(position);

  const legacyWorks = getIdArray(e, "work_path_id");
  const hasWorks = worksEmployeeIds.has((e as any).id) || legacyWorks.length > 0;

  return (
    <Link to={`/team/${(e as any).id}`} className={s.cardLink}>
      <article className={s.card}>
        <div className={s.photo}>
          <EmployeePhoto photoId={photoId} name={name} galleryMap={galleryMap} />
        </div>

        <div className={s.info}>
          <div className={s.name}>{name}</div>

          {lines.length > 0 && (
            <div className={s.meta}>
              {lines.map((t) => (
                <div key={t} className={s.metaLine}>
                  {t}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={s.cardFooter}>
          <span className={hasWorks ? s.worksLink : s.worksLinkMuted}>
            {hasWorks ? "Выполненные работы" : "Профиль специалиста"}
          </span>
        </div>
      </article>
    </Link>
  );
}

const FALLBACK: Employee[] = [
  { id: 1, surname: "Иванова", name: "Мария" } as any,
  { id: 2, surname: "Петров", name: "Алексей" } as any,
];

export default function TeamPage() {
  const { data: employees, isLoading, isError } = useEmployees();
  const { data: gallery } = useGallery();
  const { data: employeeWorks } = useEmployeeWorks();

  const safeEmployees = Array.isArray(employees) ? employees : [];
  const safeGallery = Array.isArray(gallery) ? gallery : [];
  const safeEmployeeWorks = Array.isArray(employeeWorks) ? employeeWorks : [];

  const galleryMap = useMemo(() => {
    const m = new Map<number, Gallery>();
    safeGallery.forEach((g: Gallery) => {
      if (typeof g.id === "number") m.set(g.id, g);
    });
    return m;
  }, [safeGallery]);

  const worksEmployeeIds = useMemo(() => {
    const s = new Set<number>();
    safeEmployeeWorks.forEach((w: EmployeeWork) => {
      if (typeof w.employee_id === "number") {
        s.add(w.employee_id);
      }
    });
    return s;
  }, [safeEmployeeWorks]);

  const hasDbData = !isLoading && !isError && safeEmployees.length > 0;
  const list = hasDbData ? safeEmployees : FALLBACK;
  const usingFallback = !hasDbData;

  return (
    <div className={s.pageWrap}>
      <Header />

      <main className={s.main}>
        <section className="section section--tight">
          <Container className={s.wide}>
            <div className={s.panel}>
              <h1 className={s.h1}>Команда</h1>

              <p className={s.p}>
                Врачи и специалисты клиники. Нажмите на карточку, чтобы открыть страницу врача,
                посмотреть профиль, сертификаты и выполненные работы.
              </p>

              {usingFallback && (
                <div className={s.pageMeta}>
                  Сейчас показаны тестовые данные (таблица Employee пустая или сервер недоступен).
                </div>
              )}

              {isLoading && <div className={s.pageMeta}>Загрузка сотрудников…</div>}
              {isError && <div className={s.pageMeta}>Нет связи с сервером (показаны тестовые данные)</div>}

              <div className={s.grid}>
                {list.map((e) => (
                  <EmployeeCard
                    key={(e as any).id}
                    e={e}
                    galleryMap={galleryMap}
                    worksEmployeeIds={worksEmployeeIds}
                  />
                ))}
              </div>
            </div>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
}
